import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Check, 
  Zap, 
  Building2, 
  Users,
  Loader2,
  Crown,
  Rocket,
  Shield,
  Ticket,
  Heart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [hardshipDialogOpen, setHardshipDialogOpen] = useState(false);
  const [hardshipReason, setHardshipReason] = useState('');
  const [hardshipInfo, setHardshipInfo] = useState('');
  const [hardshipProofFile, setHardshipProofFile] = useState(null);
  const [applyingHardship, setApplyingHardship] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      currency: '£',
      icon: Heart,
      color: 'bg-[#5DBDB3]',
      description: 'Free for everyone who creates an account',
      features: [
        'Unlimited barcode scanning',
        'Common allergen detection',
        'AI-powered analysis',
        'Unlimited scan history',
        'Community support'
      ],
      popular: false,
      isFree: true
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 69,
      currency: '£',
      icon: Rocket,
      color: 'bg-[#F08B7D]',
      description: 'For growing companies and food chains',
      features: [
        'Everything in Free',
        'Hidden ingredients detection',
        'Team management (5 members)',
        'Bulk analysis',
        '5,000 API calls/month',
        'Priority support'
      ],
      popular: true
    },
    {
      id: 'festival',
      name: 'Festival',
      price: 149,
      currency: '£',
      icon: Ticket,
      color: 'bg-purple-500',
      description: 'For festival organizers & events',
      features: [
        'Create festival events',
        'Color-coded wristbands',
        'Printable wristband PDFs',
        'Vendor display boards',
        'QR code scanning',
        'Bulk attendee registration'
      ],
      popular: false
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 199,
      currency: '£',
      icon: Building2,
      color: 'bg-[#5DBDB3]',
      description: 'For large organizations with custom needs',
      features: [
        'Everything in Pro + Festival',
        'Unlimited scans & events',
        'White-label reports',
        'Unlimited API calls',
        'Custom integrations',
        'Dedicated support'
      ],
      popular: false
    }
  ];

  const handleApplyHardship = async () => {
    if (!hardshipReason.trim()) {
      toast.error('Please select your situation');
      return;
    }
    
    if (!hardshipProofFile) {
      toast.error('Please upload proof of your benefits');
      return;
    }
    
    setApplyingHardship(true);
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('reason', hardshipReason);
      formData.append('additional_info', hardshipInfo || '');
      formData.append('proof_file', hardshipProofFile);
      
      await axios.post(`${API}/user/hardship-access`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Application submitted! We will review your proof and notify you within 24-48 hours.');
      setHardshipDialogOpen(false);
      setHardshipReason('');
      setHardshipInfo('');
      setHardshipProofFile(null);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplyingHardship(false);
    }
  };

  // Check for payment success
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Subscription activated successfully!');
        navigate('/company');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingStatus(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [token, refreshUser]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleSubscribe = async (planId) => {
    if (!planId) return;
    
    setLoading(true);
    setSelectedPlan(planId);
    
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
        company_name: companyName || null,
        industry: industry || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
          <h2 className="text-xl font-semibold mb-2">Verifying Payment</h2>
          <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="subscription-page">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">
            <Building2 className="w-3 h-3 mr-1" />
            For Companies & Food Chains
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Unlock powerful features for your business including bulk analysis, 
            team management, and API access for your applications.
          </p>
        </div>

        {/* Company Info (shown before selecting plan) */}
        {!user?.subscription_plan && (
          <div className="max-w-md mx-auto mb-12">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle className="text-lg">Company Information</CardTitle>
                <CardDescription>Optional - helps us personalize your experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    placeholder="Your Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    placeholder="e.g., Food Manufacturing, Retail"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    data-testid="industry-input"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative border ${
                plan.popular 
                  ? 'border-primary shadow-lg scale-105' 
                  : 'border-border'
              } ${
                user?.subscription_plan === plan.id 
                  ? 'ring-2 ring-primary' 
                  : ''
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#5DBDB3]">Most Popular</Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className={`w-12 h-12 rounded-xl ${plan.color} flex items-center justify-center mx-auto mb-4`}>
                  <plan.icon className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="text-center">
                <div className="mb-6">
                  {plan.isFree ? (
                    <span className="text-4xl font-bold text-[#5DBDB3]">FREE</span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold">{plan.currency}{plan.price}</span>
                      <span className="text-muted-foreground">/month</span>
                    </>
                  )}
                </div>
                
                <ul className="space-y-3 text-left">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#5DBDB3] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                {user?.subscription_plan === plan.id ? (
                  <Button className="w-full bg-[#5DBDB3]" disabled>
                    <Check className="w-4 h-4 mr-2" />
                    Current Plan
                  </Button>
                ) : plan.isFree ? (
                  <Button 
                    className="w-full bg-[#5DBDB3] hover:bg-[#4DA9A0]"
                    onClick={() => navigate('/auth')}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    Create Free Account
                  </Button>
                ) : (
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-[#5DBDB3] hover:bg-[#4DA9A0]' : ''}`}
                    variant={plan.popular ? "default" : "outline"}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>Get Started</>
                    )}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Hardship Access Section - Hidden Ingredients for Low Income Families */}
        <div className="mt-12 max-w-2xl mx-auto">
          <Card className="border-2 border-dashed border-[#F08B7D]/50 bg-[#F08B7D]/5">
            <CardHeader className="text-center">
              <div className="w-12 h-12 rounded-xl bg-[#F08B7D] flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <CardTitle>Hidden Ingredients for Low Income Families</CardTitle>
              <CardDescription className="text-base">
                We believe everyone deserves to know what&apos;s <strong>really</strong> in their food. 
                If you&apos;re on benefits or experiencing financial hardship, we offer <strong>priority access</strong> to 
                our advanced hidden ingredients detection — designed to protect families with rare allergies.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Simply upload proof of your benefits (letter, screenshot, or document) and we&apos;ll 
                review your application within 24-48 hours. Your family&apos;s health shouldn&apos;t depend on income.
              </p>
              <Dialog open={hardshipDialogOpen} onOpenChange={setHardshipDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-[#F08B7D] text-[#F08B7D] hover:bg-[#F08B7D]/10"
                    data-testid="hardship-access-btn"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Apply for Free Hardship Access
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Hardship Access Application</DialogTitle>
                    <DialogDescription>
                      Please provide proof of your benefits. We&apos;ll review your application and notify you within 24-48 hours.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Your Situation *</Label>
                      <select
                        value={hardshipReason}
                        onChange={(e) => setHardshipReason(e.target.value)}
                        className="w-full p-2 border border-border rounded-md bg-background"
                        data-testid="hardship-reason-select"
                      >
                        <option value="">Select your situation...</option>
                        <option value="Universal Credit">Receiving Universal Credit</option>
                        <option value="PIP">Receiving PIP (Personal Independence Payment)</option>
                        <option value="Housing Benefit">Receiving Housing Benefit</option>
                        <option value="ESA">Receiving ESA (Employment and Support Allowance)</option>
                        <option value="Pension Credit">Receiving Pension Credit</option>
                        <option value="Income Support">Receiving Income Support</option>
                        <option value="Jobseeker">Receiving Jobseeker&apos;s Allowance</option>
                        <option value="Council Tax Support">Receiving Council Tax Support</option>
                        <option value="Other">Other financial hardship</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Upload Proof of Benefits *</Label>
                      <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx"
                          onChange={(e) => setHardshipProofFile(e.target.files[0])}
                          className="hidden"
                          id="proof-upload"
                          data-testid="hardship-proof-upload"
                        />
                        <label 
                          htmlFor="proof-upload" 
                          className="cursor-pointer block"
                        >
                          {hardshipProofFile ? (
                            <div className="text-sm">
                              <Check className="w-6 h-6 text-green-500 mx-auto mb-2" />
                              <p className="font-medium text-green-600">{hardshipProofFile.name}</p>
                              <p className="text-muted-foreground">Click to change file</p>
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              <Shield className="w-8 h-8 mx-auto mb-2 text-[#F08B7D]" />
                              <p className="font-medium">Click to upload proof</p>
                              <p>Benefits letter, DWP screenshot, or official document</p>
                              <p className="text-xs mt-1">PNG, JPG, PDF up to 10MB</p>
                            </div>
                          )}
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Your document is kept secure and only used for verification.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Additional Information (Optional)</Label>
                      <Textarea
                        placeholder="Anything else you'd like us to know..."
                        value={hardshipInfo}
                        onChange={(e) => setHardshipInfo(e.target.value)}
                        data-testid="hardship-info-input"
                        className="min-h-[60px]"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleApplyHardship}
                      className="w-full bg-[#5DBDB3] hover:bg-[#4DA9A0]"
                      disabled={applyingHardship || !hardshipReason || !hardshipProofFile}
                      data-testid="submit-hardship-btn"
                    >
                      {applyingHardship ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting Application...
                        </>
                      ) : (
                        <>Submit Application</>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>

        {/* Features Comparison */}
        <div className="mt-16">
          <h2 className="text-2xl font-semibold text-center mb-8">All Plans Include</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'AI-Powered Analysis', desc: 'GPT-5.2 ingredient analysis' },
              { icon: Users, title: 'Team Collaboration', desc: 'Share reports with your team' },
              { icon: Zap, title: 'API Access', desc: 'Integrate with your apps' },
              { icon: Building2, title: 'White-Label Ready', desc: 'Custom branding options' }
            ].map((item) => (
              <Card key={item.title} className="border border-border text-center p-6">
                <item.icon className="w-8 h-8 mx-auto mb-3 text-[#5DBDB3]" />
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Can I cancel anytime?', a: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of the billing period.' },
              { q: 'How does the API work?', a: 'After subscribing, you\'ll receive an API key that allows your applications to analyze ingredients programmatically.' },
              { q: 'What payment methods do you accept?', a: 'We accept all major credit cards through our secure Stripe payment processing.' },
              { q: 'Can I upgrade or downgrade?', a: 'Yes, you can change your plan at any time. Changes take effect at the start of your next billing cycle.' }
            ].map((faq) => (
              <Card key={faq.q} className="border border-border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">{faq.q}</h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
